import { useState, useRef, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import Avvvatars from "avvvatars-react";
import Icon1 from "@assets/icons/Icon";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import ChatPanel from "@components/chat/ChatPanel";
import FloatingChatWidget from "@components/chat/FloatingChatWidget";
import SearchField from "@components/ui/textfields/SearchField";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import DatePicker from "@components/ui/selection/DatePicker";
import MenuButton from "@components/ui/buttons/MenuButton";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { Text1 } from "@components/ui/texts/Texts";
import { getDossierById, useDossierStore } from "@store/dossierStore";
import { dossiersApi, type DocumentClassification } from "@services/api/dossiers";
import { ordresApi } from "@services/api/ordres";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import type {
  Dossier,
  DossierTimelineEvent,
  DossierDocument,
  DossierOrdreSummary,
} from "types/dossier";
import type { Contact } from "types/contact";
import type { ClientVehicle } from "types/client_vehicle";
import type { Ordre, OrdreDocument } from "types/ordre";

// ─── Constants ─────────────────────────────────────────────

const COLORIS_TO_PAINT: Record<string, string> = {
  noir: "black", blanc: "white", gris: "grey", bleu: "blue",
  rouge: "red", vert: "green", jaune: "yellow", marron: "brown",
  orange: "orange", autre: "silver",
};

function getVehicleImageUrl(marque?: string, modele?: string, coloris?: string): string | null {
  if (!marque || !modele) return null;
  const make = encodeURIComponent(marque.toLowerCase());
  const model = encodeURIComponent(modele.toLowerCase());
  const paint = COLORIS_TO_PAINT[coloris || ""] || "grey";
  return `https://cdn.imagin.studio/getimage?customer=img&make=${make}&modelFamily=${model}&modelYear=2023&zoomType=fullscreen&angle=0&paintDescription=${paint}`;
}

const CIVILITE_OPTIONS = [
  { value: "M.", label: "M." },
  { value: "Mme", label: "Mme" },
  { value: "Société", label: "Société" },
];

const PAYS_OPTIONS = [
  { value: "FRANCE", label: "FRANCE" },
  { value: "BELGIQUE", label: "BELGIQUE" },
  { value: "SUISSE", label: "SUISSE" },
  { value: "LUXEMBOURG", label: "LUXEMBOURG" },
];

const COLORIS_OPTIONS = [
  { value: "noir", label: "Noir" }, { value: "blanc", label: "Blanc" },
  { value: "gris", label: "Gris" }, { value: "bleu", label: "Bleu" },
  { value: "rouge", label: "Rouge" }, { value: "vert", label: "Vert" },
  { value: "autre", label: "Autre" },
];

// ─── Mock detail data per dossier ──────────────────────────

const MOCK_DETAIL: Record<string, Partial<Dossier>> = {
  "dos-1": {
    contacts: [
      { id: "c1", dossier_id: "dos-1", nom: "DESCHAMPS PATRICK & MAISON", prenom: "François", civilite: "M.", pays: "FRANCE", phone: "06 08 97 77 14", email: "francois@maison.fr", is_primary: true, created_at: "2025-01-01T00:00:00Z" },
      { id: "c2", dossier_id: "dos-1", nom: "MARTIN", prenom: "Michel", civilite: "M.", pays: "FRANCE", phone: "06 98 76 54 32", is_primary: false, created_at: "2025-01-01T00:00:00Z" },
    ],
    vehicles: [
      { id: "v1", org_id: "org1", dossier_id: "dos-1", plate: "EE-249-GR", marque: "PEUGEOT", modele: "5008", coloris: "blanc", km: 77025, vin: "WF0DXXGAKDGC01845", mec: 1785340800, created_at: "2025-01-01T00:00:00Z" },
      { id: "v2", org_id: "org1", dossier_id: "dos-1", plate: "FF-350-HS", marque: "PEUGEOT", modele: "508", coloris: "noir", km: 45200, created_at: "2025-01-01T00:00:00Z" },
    ],
    documents: [
      { id: "d1", name: "Vue en plan", type: "pdf", created_at: "18/12/2025" },
      { id: "d2", name: "Façades", type: "pdf", created_at: "18/12/2025" },
      { id: "d3", name: "Perspectives", type: "pdf", created_at: "18/12/2025" },
      { id: "d4", name: "Fondations", type: "pdf", created_at: "18/12/2025" },
      { id: "d5", name: "Carte grise", type: "pdf", created_at: "14/12/2025" },
      { id: "d6", name: "Assurance", type: "pdf", created_at: "14/12/2025" },
    ],
    timeline: [
      { id: "ev1", date: "2025-12-18T09:54:00Z", user_name: "Freddy", user_color: "#10b981", action: "a ajouté 4 documents", type: "documents", document_names: ["Vue en plan", "Façades", "Perspectives", "Fondations"] },
      { id: "ev2", date: "2025-12-15T10:30:00Z", user_name: "Freddy", user_color: "#10b981", action: "a ajouté 3 photos", type: "photos", photo_urls: ["https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=200&h=150&fit=crop", "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=200&h=150&fit=crop", "https://images.unsplash.com/photo-1541899481282-d53bffe3c35d?w=200&h=150&fit=crop"] },
      { id: "ev3", date: "2025-12-14T08:10:00Z", user_name: "Lana", user_color: "#8b5cf6", action: "a changé le status de l'OR IVSM48", type: "status_change", status_label: "Passé en Commande", status_type: "success", ordre_ref: "OR IVSM48" },
      { id: "ev4", date: "2025-12-14T08:02:00Z", user_name: "M. Martin", user_color: "#6366f1", action: "a ajouté 2 documents", type: "documents", document_names: ["Carte grise", "Assurance"] },
    ],
    ordres: [
      { id: "o1", num: "URSOCK1", type: "MECA", vehicle_name: "Véhicule 1", status: "en prépa.", status_color: "#A1A1A1", created_at: "02/03/2026 14:00", updated_at: "02/03/2026 16:00" },
      { id: "o2", num: "URSPOCK", type: "MECA", vehicle_name: "Véhicule 2", status: "en cours", status_color: "#3b82f6", created_at: "02/03/2026 14:00", updated_at: "02/03/2026 16:00" },
      { id: "o3", num: "APM54FT", type: "MECA", vehicle_name: "Véhicule 1", status: "restitué", status_color: "#10b981", created_at: "02/03/2026 14:00", updated_at: "02/03/2026 16:00" },
    ],
  },
  "dos-2": {
    contacts: [
      { id: "c3", dossier_id: "dos-2", nom: "MARTIN", prenom: "Sophie", civilite: "Mme", pays: "FRANCE", phone: "06 98 76 54 32", email: "s.martin@email.com", is_primary: true, created_at: "2025-01-01T00:00:00Z" },
    ],
    vehicles: [
      { id: "v3", org_id: "org1", dossier_id: "dos-2", plate: "GH-789-IJ", marque: "RENAULT", modele: "CLIO", coloris: "rouge", km: 62000, created_at: "2025-01-01T00:00:00Z" },
    ],
    documents: [
      { id: "d7", name: "Carte grise", type: "pdf", created_at: "15/12/2025" },
      { id: "d8", name: "Devis réparation", type: "pdf", created_at: "16/12/2025" },
    ],
    timeline: [
      { id: "ev5", date: "2025-12-17T14:20:00Z", user_name: "Marc", user_color: "#f59e0b", action: "a créé un devis", type: "status_change", status_label: "Devis envoyé", status_type: "info" },
      { id: "ev6", date: "2025-12-15T09:00:00Z", user_name: "Sophie Martin", user_color: "#6366f1", action: "a ajouté 1 document", type: "documents", document_names: ["Carte grise"] },
    ],
    ordres: [
      { id: "o4", num: "OR-2024-002", type: "MECA", vehicle_name: "Véhicule 1", status: "devis", status_color: "#f59e0b", created_at: "15/12/2025 09:00", updated_at: "17/12/2025 14:20" },
    ],
  },
  "dos-3": {
    contacts: [
      { id: "c4", dossier_id: "dos-3", nom: "LEROY", prenom: "Thomas", civilite: "M.", pays: "FRANCE", phone: "01 42 33 44 55", email: "t.leroy@garagecentral.fr", is_primary: true, created_at: "2025-01-01T00:00:00Z" },
      { id: "c5", dossier_id: "dos-3", nom: "RENARD", prenom: "Julie", civilite: "Mme", pays: "FRANCE", email: "compta@garagecentral.fr", is_primary: false, created_at: "2025-01-01T00:00:00Z" },
    ],
    vehicles: [
      { id: "v4", org_id: "org1", dossier_id: "dos-3", plate: "KL-012-MN", marque: "PEUGEOT", modele: "308", coloris: "blanc", km: 55000, created_at: "2025-01-01T00:00:00Z" },
      { id: "v5", org_id: "org1", dossier_id: "dos-3", plate: "OP-345-QR", marque: "CITROEN", modele: "BERLINGO VP", coloris: "gris", km: 89000, created_at: "2025-01-01T00:00:00Z" },
      { id: "v6", org_id: "org1", dossier_id: "dos-3", plate: "ST-678-UV", marque: "RENAULT", modele: "KANGOO", coloris: "blanc", km: 120000, created_at: "2025-01-01T00:00:00Z" },
    ],
    documents: [
      { id: "d9", name: "Contrat leasing", type: "pdf", created_at: "10/12/2025" },
      { id: "d10", name: "Facture entretien #1204", type: "pdf", created_at: "14/12/2025" },
      { id: "d11", name: "PV contrôle technique", type: "pdf", created_at: "16/12/2025" },
    ],
    timeline: [
      { id: "ev7", date: "2025-12-16T11:30:00Z", user_name: "Lana", user_color: "#8b5cf6", action: "a ajouté le PV contrôle technique", type: "documents", document_names: ["PV contrôle technique"] },
      { id: "ev8", date: "2025-12-14T16:45:00Z", user_name: "Marc", user_color: "#f59e0b", action: "a terminé l'entretien du Berlingo", type: "status_change", status_label: "Entretien terminé", status_type: "success" },
      { id: "ev9", date: "2025-12-10T08:00:00Z", user_name: "Thomas Leroy", user_color: "#10b981", action: "a déposé le contrat leasing", type: "documents", document_names: ["Contrat leasing"] },
    ],
    ordres: [
      { id: "o5", num: "OR-FL-001", type: "MECA", vehicle_name: "Véhicule 2", status: "en cours", status_color: "#3b82f6", created_at: "10/12/2025 08:00", updated_at: "14/12/2025 16:45" },
    ],
  },
  "dos-4": {
    contacts: [
      { id: "c6", dossier_id: "dos-4", nom: "DUBOIS", prenom: "Pierre", civilite: "M.", pays: "FRANCE", phone: "07 11 22 33 44", is_primary: true, created_at: "2025-01-01T00:00:00Z" },
    ],
    vehicles: [
      { id: "v7", org_id: "org1", dossier_id: "dos-4", plate: "WX-901-YZ", marque: "VOLKSWAGEN", modele: "GOLF", coloris: "noir", km: 38000, created_at: "2025-01-01T00:00:00Z" },
    ],
    documents: [
      { id: "d12", name: "Constat amiable", type: "pdf", created_at: "12/12/2025" },
    ],
    timeline: [
      { id: "ev10", date: "2025-12-15T10:00:00Z", user_name: "Pierre Dubois", user_color: "#ef4444", action: "a demandé un suivi de réclamation", type: "comment" },
      { id: "ev11", date: "2025-12-12T14:30:00Z", user_name: "Freddy", user_color: "#10b981", action: "a ajouté 2 photos", type: "photos", photo_urls: ["https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=200&h=150&fit=crop", "https://images.unsplash.com/photo-1549317661-bd32c8ce0afa?w=200&h=150&fit=crop"] },
      { id: "ev12", date: "2025-12-12T09:15:00Z", user_name: "Pierre Dubois", user_color: "#ef4444", action: "a ajouté le constat amiable", type: "documents", document_names: ["Constat amiable"] },
    ],
    ordres: [],
  },
  "dos-5": {
    contacts: [
      { id: "c7", dossier_id: "dos-5", nom: "LEVY", prenom: "David", civilite: "M.", pays: "FRANCE", phone: "01 55 66 77 88", email: "d.levy@levy-transport.fr", is_primary: true, created_at: "2025-01-01T00:00:00Z" },
      { id: "c8", dossier_id: "dos-5", nom: "COHEN", prenom: "Sarah", civilite: "Mme", pays: "FRANCE", phone: "01 55 66 77 89", email: "flotte@levy-transport.fr", is_primary: false, created_at: "2025-01-01T00:00:00Z" },
    ],
    vehicles: [
      { id: "v8", org_id: "org1", dossier_id: "dos-5", plate: "AA-111-BB", marque: "RENAULT", modele: "KANGOO", coloris: "blanc", km: 95000, created_at: "2025-01-01T00:00:00Z" },
      { id: "v9", org_id: "org1", dossier_id: "dos-5", plate: "CC-222-DD", marque: "PEUGEOT", modele: "BIPPER", coloris: "blanc", km: 110000, created_at: "2025-01-01T00:00:00Z" },
    ],
    documents: [
      { id: "d14", name: "Bon de commande pièces", type: "pdf", created_at: "13/12/2025" },
    ],
    timeline: [
      { id: "ev13", date: "2025-12-14T09:00:00Z", user_name: "Marc", user_color: "#f59e0b", action: "a commandé les pièces de rechange", type: "status_change", status_label: "Pièces commandées", status_type: "info" },
      { id: "ev14", date: "2025-12-13T15:00:00Z", user_name: "Sarah Cohen", user_color: "#10b981", action: "a déposé le bon de commande", type: "documents", document_names: ["Bon de commande pièces"] },
    ],
    ordres: [
      { id: "o6", num: "OR-TR-001", type: "MECA", vehicle_name: "Véhicule 1", status: "en prépa.", status_color: "#A1A1A1", created_at: "13/12/2025 15:00", updated_at: "14/12/2025 09:00" },
    ],
  },
};

function getEnrichedDossier(id: string): Dossier | null {
  const base = getDossierById(id);
  if (!base) return null;
  const detail = MOCK_DETAIL[id];
  return {
    ...base,
    contacts: base.contacts?.length ? base.contacts : detail?.contacts || [],
    vehicles: base.vehicles?.length ? base.vehicles : detail?.vehicles || [],
    documents: base.documents?.length ? base.documents : detail?.documents || [],
    timeline: base.timeline?.length ? base.timeline : detail?.timeline || [],
    ordres: base.ordres?.length ? base.ordres : detail?.ordres || [],
  };
}

// ─── Mock chat messages ────────────────────────────────────


// ─── Timeline component ────────────────────────────────────

function formatEventDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

const IMAGE_NAME_RE = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

interface AllDocEntry { id: string; name: string; gcs_path?: string; parentType: "dossier" | "ordre"; parentId: string }

function DossierTimelineDocThumbnail({ docName, allDocs }: { docName: string; allDocs: AllDocEntry[] }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const doc = allDocs.find((d) => d.name === docName);
    if (!doc || !doc.gcs_path) return;
    let cancelled = false;
    const fetchUrl = doc.parentType === "ordre"
      ? ordresApi.getDocumentUrl(doc.parentId, doc.id)
      : dossiersApi.getDocumentUrl(doc.parentId, doc.id);
    fetchUrl.then((u) => { if (!cancelled) setUrl(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, [docName, allDocs]);

  return (
    <div className="w-[80px] h-[80px] rounded-lg overflow-hidden bg-neutral-100 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0">
      {url ? (
        <img src={url} alt={docName} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Icon1 name="image" size={24} color="#D4D4D4" />
        </div>
      )}
    </div>
  );
}

function TimelineEvent({ event, isLast, onDocClick, allDocs }: { event: DossierTimelineEvent; isLast: boolean; onDocClick?: (docName: string) => void; allDocs?: AllDocEntry[] }) {
  const imageNames = (event.type === "documents" && event.document_names)
    ? event.document_names.filter((n) => IMAGE_NAME_RE.test(n))
    : [];
  const fileNames = (event.type === "documents" && event.document_names)
    ? event.document_names.filter((n) => !IMAGE_NAME_RE.test(n))
    : [];

  return (
    <div className="flex flex-row">
      <div className="flex-shrink-0 text-h3 text-neutral-400" style={{ width: 90, paddingTop: 2, textAlign: "right", paddingRight: 12 }}>
        {formatEventDate(event.date)}
      </div>
      <div className="flex flex-col items-center flex-shrink-0" style={{ width: 20 }}>
        <div className="rounded-full mt-1.5 flex-shrink-0" style={{ width: 10, height: 10, background: "#A3A3A3" }} />
        {!isLast && <div className="w-0.5 bg-neutral-300 flex-1 my-1" />}
      </div>
      <div className="flex flex-col gap-2 ml-3 flex-1 pb-6" style={{ minWidth: 0 }}>
        <div className="flex flex-row gap-2 items-start">
          <div className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999 }}>
            <Avvvatars value={event.user_name} style="shape" size={32} />
          </div>
          <div className="flex flex-col">
            <span className="text-h2 font-semibold" style={{ lineHeight: "18px" }}>{event.user_name}</span>
            <span className="text-h3 text-neutral-400">{event.action}</span>
          </div>
        </div>
        {imageNames.length > 0 && allDocs && (
          <div className="flex flex-row gap-2 mt-1 flex-wrap">
            {imageNames.map((name, i) => (
              <DossierTimelineDocThumbnail key={i} docName={name} allDocs={allDocs} />
            ))}
          </div>
        )}
        {fileNames.length > 0 && (
          <div className="flex flex-col gap-0.5 mt-1">
            {fileNames.map((name, i) => (
              <div
                key={i}
                className="flex flex-row items-center gap-2 cursor-pointer hover:bg-neutral-50 rounded px-1 py-0.5"
                onClick={() => onDocClick?.(name)}
              >
                <Icon1 name="file" size={14} color="#6366f1" />
                <span className="text-h3 text-indigo-600 hover:underline">{name}</span>
              </div>
            ))}
          </div>
        )}
        {event.type === "photos" && event.photo_urls && (
          <div className="flex flex-row gap-2 mt-1 flex-wrap">
            {event.photo_urls.map((url, i) => (
              <div key={i} className="w-[80px] h-[80px] rounded-lg overflow-hidden bg-neutral-100 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0">
                <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        )}
        {event.type === "status_change" && event.status_label && (
          <div className="flex flex-row items-center gap-2 mt-1">
            <span className="text-h3 font-medium" style={{ color: event.status_type === "success" ? "#10b981" : event.status_type === "warning" ? "#f59e0b" : "#525252" }}>
              {event.status_label}
            </span>
            <Icon1 name={event.status_type === "success" ? "done" : "horloge"} size={16} color={event.status_type === "success" ? "#10b981" : "#525252"} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Fil du dossier tab ────────────────────────────────────

function FilDuDossierTab({ timeline, vehicles, search, onDocClick, allDocs }: { timeline: DossierTimelineEvent[]; vehicles: ClientVehicle[]; search: string; onDocClick?: (docName: string) => void; allDocs?: AllDocEntry[] }) {
  const [vehicleFilter, setVehicleFilter] = useState<string>("all");
  const filtered = timeline.filter((ev) => {
    if (vehicleFilter !== "all" && ev.vehicle_id && ev.vehicle_id !== vehicleFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return ev.user_name.toLowerCase().includes(q) || ev.action.toLowerCase().includes(q) || (ev.document_names || []).some((n) => n.toLowerCase().includes(q)) || (ev.status_label || "").toLowerCase().includes(q);
    }
    return true;
  });
  const sorted = [...filtered].sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <div className="flex flex-col gap-3">
      {vehicles.length > 0 && (
        <div className="flex flex-row gap-2">
          {vehicles.map((v) => (
            <button
              key={v.id}
              className="px-3 py-1.5 rounded-lg text-h3 font-medium transition-colors border"
              style={{
                background: vehicleFilter === v.id ? "#262626" : "#FFFFFF",
                color: vehicleFilter === v.id ? "#FFFFFF" : "#525252",
                borderColor: vehicleFilter === v.id ? "#262626" : "#E5E5E5",
              }}
              onClick={() => setVehicleFilter(vehicleFilter === v.id ? "all" : v.id)}
            >
              {v.marque} {v.modele}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-col">
        {sorted.length === 0 ? (
          <div className="text-h3 text-center text-neutral-400 py-8">{search ? "Aucun résultat" : "Aucun événement"}</div>
        ) : sorted.map((ev, i) => <TimelineEvent key={ev.id} event={ev} isLast={i === sorted.length - 1} onDocClick={onDocClick} allDocs={allDocs} />)}
      </div>
    </div>
  );
}

// ─── Contacts tab (editable form) ──────────────────────────

function ContactsTab({ contacts: initial, dossierId }: { contacts: Contact[]; dossierId: string }) {
  const [contacts, setContacts] = useState<Contact[]>(initial);
  const [saving, setSaving] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveContacts = (updated: Contact[]) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await dossiersApi.update(dossierId, { contacts: updated } as any);
      } catch {
        showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
      } finally {
        setSaving(false);
      }
    }, 800);
  };

  const updateContact = (id: string, field: keyof Contact, value: string) => {
    setContacts((prev) => {
      const updated = prev.map((c) => (c.id === id ? { ...c, [field]: value } : c));
      saveContacts(updated);
      return updated;
    });
  };

  const addContact = () => {
    setContacts((prev) => {
      const updated: Contact[] = [...prev, { id: `c-new-${Date.now()}`, dossier_id: dossierId, nom: "", prenom: "", civilite: "M." as const, pays: "FRANCE", phone: "", email: "", is_primary: false, created_at: new Date().toISOString() }];
      saveContacts(updated);
      return updated;
    });
  };

  return (
    <div className="flex flex-col gap-6">
      {contacts.map((c, idx) => (
        <div key={c.id} className="flex flex-col gap-3 border border-neutral-200 rounded-xl p-4">
          <div className="flex flex-row items-center justify-between">
            <span className="text-h3 font-medium text-neutral-500">Contact {idx + 1}</span>
            {saving && idx === 0 && <span className="text-h4 text-neutral-400">Sauvegarde...</span>}
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TextField label="Nom" value={c.nom} onChange={(v) => updateContact(c.id, "nom", v)} />
            </div>
            <div style={{ width: 140 }}>
              <DropDown label="Civilité" options={CIVILITE_OPTIONS} value={c.civilite || ""} onSelect={(v) => updateContact(c.id, "civilite", String(v))} />
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TextField label="Prénom" value={c.prenom || ""} onChange={(v) => updateContact(c.id, "prenom", v)} />
            </div>
            <div className="flex-1">
              <DropDown label="Pays" options={PAYS_OPTIONS} value={c.pays || ""} onSelect={(v) => updateContact(c.id, "pays", String(v))} />
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TextField label="Téléphone" value={c.phone || ""} onChange={(v) => updateContact(c.id, "phone", v)} />
            </div>
            <div className="flex-1">
              <TextField label="Email" value={c.email || ""} onChange={(v) => updateContact(c.id, "email", v)} />
            </div>
          </div>
        </div>
      ))}
      <button
        className="flex flex-row items-center gap-2 text-h3 font-medium text-neutral-600 border border-neutral-200 rounded-lg px-4 py-2 w-fit hover:bg-neutral-50"
        onClick={addContact}
      >
        <Icon1 name="plus" size={14} color="#525252" />
        Ajouter un contact
      </button>
    </div>
  );
}

// ─── Ordres tab (table) ────────────────────────────────────

function OrdresTab({ ordres }: { ordres: DossierOrdreSummary[] }) {
  return (
    <div className="flex flex-col gap-3">
      <span className="text-h3 font-medium text-neutral-500">
        Ordres de réparations liés ({ordres.length})
      </span>
      {ordres.length === 0 ? (
        <div className="text-h3 text-center text-neutral-400 py-8">Aucun ordre lié</div>
      ) : (
        <div className="border border-neutral-200 rounded-xl overflow-x-auto overflow-y-hidden">
          <table className="w-full" style={{ minWidth: 500 }}>
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                <th className="text-left text-h4 font-medium text-neutral-500 px-3 py-2">Dates</th>
                <th className="text-left text-h4 font-medium text-neutral-500 px-3 py-2">Ordre</th>
                <th className="text-left text-h4 font-medium text-neutral-500 px-3 py-2">Type</th>
                <th className="text-left text-h4 font-medium text-neutral-500 px-3 py-2">Véhicule</th>
                <th className="text-right text-h4 font-medium text-neutral-500 px-3 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {ordres.map((o) => (
                <tr key={o.id} className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer" onClick={() => navigateTo({ route: `/ordre/${o.id}` })}>
                  <td className="text-h4 text-neutral-400 px-3 py-2.5">
                    <div>{o.created_at}</div>
                    <div>{o.updated_at}</div>
                  </td>
                  <td className="text-h3 font-medium px-3 py-2.5">{o.num}</td>
                  <td className="text-h3 text-neutral-500 px-3 py-2.5">{o.type}</td>
                  <td className="text-h3 text-neutral-500 px-3 py-2.5">{o.vehicle_name}</td>
                  <td className="text-right px-3 py-2.5">
                    <span
                      className="text-h4 font-medium px-3 py-1 rounded-full"
                      style={{
                        color: o.status_color,
                        background: o.status_color + "18",
                      }}
                    >
                      {o.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Véhicules tab (expanded cards with form) ──────────────

function VehiculesTab({ vehicles: initial, dossierId }: { vehicles: ClientVehicle[]; dossierId: string }) {
  const [vehicles, setVehicles] = useState<ClientVehicle[]>(initial);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveVehicles = (updated: ClientVehicle[]) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await dossiersApi.update(dossierId, { vehicles: updated } as any);
      } catch {
        showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
      }
    }, 800);
  };

  const updateVehicle = (id: string, field: keyof ClientVehicle, value: any) => {
    setVehicles((prev) => {
      const updated = prev.map((v) => (v.id === id ? { ...v, [field]: value } : v));
      saveVehicles(updated);
      return updated;
    });
    if (field === "marque" || field === "modele" || field === "coloris") {
      setImageErrors((prev) => ({ ...prev, [id]: false }));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {vehicles.map((v) => {
        const imgUrl = getVehicleImageUrl(v.marque, v.modele, v.coloris);
        return (
          <div key={v.id} className="flex flex-col gap-3">
            {/* Header */}
            <div className="flex flex-row items-center gap-2">
              <span className="text-h2 font-semibold">{v.plate}</span>
              <span className="text-h3 text-neutral-400">{">"}</span>
              <span className="text-h3 text-neutral-400">{v.marque} {v.modele}</span>
            </div>

            {/* Car image */}
            <div className="w-full rounded-xl border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden" style={{ height: 180 }}>
              {imgUrl && !imageErrors[v.id] ? (
                <img src={imgUrl} alt={v.plate} className="w-full h-full object-contain" onError={() => setImageErrors((prev) => ({ ...prev, [v.id]: true }))} />
              ) : (
                <div className="flex flex-col items-center gap-2 text-neutral-400">
                  <Icon1 name="image" size={40} color="#D4D4D4" />
                  <span className="text-h3 font-medium">Image non disponible</span>
                </div>
              )}
            </div>

            {/* Form fields */}
            <div className="flex flex-row gap-2">
              <div className="flex-1">
                <TextField label="Immatriculation" value={v.plate || ""} onChange={(val) => updateVehicle(v.id, "plate", val)} />
              </div>
              <div className="flex-1">
                <DropDown label="Coloris" options={COLORIS_OPTIONS} value={v.coloris || ""} onSelect={(val) => updateVehicle(v.id, "coloris", String(val))} />
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <div className="flex-1">
                <TextField label="Marque" value={v.marque || ""} onChange={(val) => updateVehicle(v.id, "marque", val)} />
              </div>
              <div className="flex-1">
                <DatePicker label="MEC" seconds={v.mec || null} onChange={(s) => updateVehicle(v.id, "mec", s)} />
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <div className="flex-1">
                <TextField label="Modèle" value={v.modele || ""} onChange={(val) => updateVehicle(v.id, "modele", val)} />
              </div>
              <div className="flex-1">
                <TextField label="VIN" value={v.vin || ""} onChange={(val) => updateVehicle(v.id, "vin", val)} />
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <div className="flex-1">
                <TextField label="Immatriculation" value={v.plate} onChange={(val) => updateVehicle(v.id, "plate", val)} />
              </div>
              <div className="flex-1">
                <TextField label="Km" type="number" value={String(v.km || "")} onChange={(val) => updateVehicle(v.id, "km", Number(val) || 0)} />
              </div>
            </div>

            {/* Separator */}
            <div className="border-b border-neutral-200 mt-2" />
          </div>
        );
      })}
      {vehicles.length === 0 && (
        <div className="text-h3 text-center text-neutral-400 py-8">Aucun véhicule</div>
      )}
    </div>
  );
}

// ─── Documents tab ─────────────────────────────────────────

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

function isImageDoc(d: { type: string; name: string; gcs_path?: string }) {
  return d.type === "image" || IMAGE_EXTENSIONS.test(d.gcs_path || d.name);
}

function DocPhotoThumbnail({ doc, onClick }: { doc: { id: string; name: string; gcs_path?: string }; onClick: () => void }) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!doc.gcs_path) return;
    const isOrdreDoc = doc.gcs_path.startsWith("ordres/");
    const parts = doc.gcs_path.split("/");
    const parentId = parts[2];
    const fetchUrl = async () => {
      try {
        const url = isOrdreDoc
          ? await ordresApi.getDocumentUrl(parentId, doc.id)
          : await dossiersApi.getDocumentUrl(parentId, doc.id);
        if (!cancelled) setThumbUrl(url);
      } catch { /* silently fail */ }
    };
    fetchUrl();
    return () => { cancelled = true; };
  }, [doc.gcs_path, doc.id]);

  return (
    <div
      className="w-[120px] h-[120px] rounded-lg overflow-hidden bg-neutral-100 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
      onClick={onClick}
    >
      {thumbUrl ? (
        <img src={thumbUrl} alt={doc.name} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <Icon1 name="image" size={24} color="#D4D4D4" />
      )}
    </div>
  );
}

interface DocSection {
  label: string;
  docs: Array<{ id: string; name: string; type: string; created_at?: string; gcs_path?: string }>;
}

function DocumentsTab({
  documents,
  ordreDocGroups,
  dossierId,
  onPreview,
}: {
  documents: DossierDocument[];
  ordreDocGroups: { vehicleName: string; ordreNum: string; ordreId: string; docs: OrdreDocument[] }[];
  dossierId: string;
  onPreview: (doc: { id: string; name: string; type: string; gcs_path?: string; created_at?: string }) => void;
}) {
  const sections: DocSection[] = [];

  // Vehicle sections from ordres
  for (const g of ordreDocGroups) {
    if (g.docs.length > 0) {
      sections.push({ label: `${g.vehicleName} (${g.ordreNum})`, docs: g.docs });
    }
  }

  // Dossier-level docs
  if (documents.length > 0) {
    sections.push({ label: "Général", docs: documents });
  }

  const totalDocs = sections.reduce((s, sec) => s + sec.docs.length, 0);

  if (totalDocs === 0) {
    return <div className="text-h3 text-center text-neutral-400 py-8">Aucun document</div>;
  }

  return (
    <div className="flex flex-col gap-6">
      {sections.map((sec) => {
        const photos = sec.docs.filter(isImageDoc);
        const files = sec.docs.filter((d) => !isImageDoc(d));
        return (
          <div key={sec.label} className="flex flex-col gap-2">
            {sections.length > 1 && (
              <span className="text-h3 font-semibold text-neutral-700">{sec.label}</span>
            )}
            {photos.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <span className="text-h4 text-neutral-400">Photos ({photos.length})</span>
                <div className="flex flex-row flex-wrap gap-2">
                  {photos.map((p) => (
                    <DocPhotoThumbnail key={p.id} doc={p} onClick={() => onPreview(p)} />
                  ))}
                </div>
              </div>
            )}
            {files.length > 0 && (
              <div className="flex flex-col gap-1">
                {photos.length > 0 && <span className="text-h4 text-neutral-400 mt-1">Documents ({files.length})</span>}
                {files.map((d) => (
                  <div
                    key={d.id}
                    className="flex flex-row items-center gap-3 p-3 border border-neutral-200 rounded-xl hover:bg-neutral-50 cursor-pointer"
                    onClick={() => onPreview(d)}
                  >
                    <div className="w-36 h-36 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                      <Icon1 name={d.type === "pdf" ? "file" : "image"} size={18} color="#A1A1A1" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-h3 font-medium truncate">{d.name}</span>
                      {d.created_at && <span className="text-h4 text-neutral-500">{d.created_at}</span>}
                    </div>
                    <Icon1 name="download" size={16} color="#A1A1A1" />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Add Document Modal ───────────────────────────────────

const DOCUMENT_TYPE_OPTIONS = [
  { value: "carte_grise", label: "Carte grise" },
  { value: "assurance", label: "Assurance" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "constat_amiable", label: "Constat amiable" },
  { value: "permis_conduire", label: "Permis de conduire" },
  { value: "devis", label: "Devis" },
  { value: "facture", label: "Facture" },
  { value: "bon_commande", label: "Bon de commande" },
  { value: "bon_livraison", label: "Bon de livraison" },
  { value: "contrat_location", label: "Contrat de location / prêt" },
  { value: "rapport_expertise", label: "Rapport d'expertise" },
  { value: "photo_vehicule", label: "Photo véhicule" },
  { value: "photo_degats", label: "Photo dégâts" },
  { value: "courrier", label: "Courrier" },
  { value: "autre", label: "Autre" },
];

function AddDocumentModal({
  dossierId,
  onClose,
  onSuccess,
}: {
  dossierId: string;
  onClose: () => void;
  onSuccess: (dossier: Dossier) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [classification, setClassification] = useState<DocumentClassification | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setDocName(selectedFile.name);

    // Auto-classify with AI
    setClassifying(true);
    try {
      const result = await dossiersApi.classifyDocument(selectedFile);
      setClassification(result);
      if (result.suggested_name) {
        // Preserve original file extension in the suggested name
        const origExt = selectedFile.name.includes(".") ? selectedFile.name.substring(selectedFile.name.lastIndexOf(".")) : "";
        const suggestedHasExt = result.suggested_name.includes(".");
        setDocName(suggestedHasExt ? result.suggested_name : result.suggested_name + origExt);
      }
      if (result.type_key) setDocType(result.type_key);
    } catch {
      // Classification failed — user can still manually select type
    } finally {
      setClassifying(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const updated = await dossiersApi.uploadDocument(dossierId, file, docName || undefined, docType || undefined);
      showNotification({ type: "success", message: "Document ajouté" });
      onSuccess(updated);
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <BaseModal
      title="Ajouter un document"
      startIcon={<Icon1 name="upload" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Drop zone */}
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${
            dragOver ? "border-indigo-400 bg-indigo-50" : "border-neutral-300 hover:bg-neutral-50"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <Icon1 name="file" size={28} color="#6366f1" />
              <span className="text-h3 font-medium text-neutral-700 text-center">{file.name}</span>
              <span className="text-h4 text-neutral-400">{(file.size / 1024).toFixed(0)} Ko</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Icon1 name="upload" size={24} color="#A1A1A1" />
              <span className="text-h3 text-neutral-400">Glisser ou cliquer pour importer</span>
              <span className="text-h4 text-neutral-300">PDF, images, documents</span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileSelect(f);
            }}
          />
        </div>

        {/* AI classification result */}
        {classifying && (
          <div className="flex flex-row items-center gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
            <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-h3 text-indigo-600">Analyse du document en cours...</span>
          </div>
        )}
        {classification && !classifying && classification.confidence > 0.3 && (
          <div className="flex flex-row items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
            <Icon1 name="done" size={16} color="#10b981" />
            <span className="text-h3 text-emerald-700">
              Document identifié : <strong>{classification.type_label}</strong>
              {classification.confidence >= 0.7 ? "" : " (incertain)"}
            </span>
          </div>
        )}

        {/* Document name */}
        <TextField label="Nom du document" value={docName} onChange={setDocName} />

        {/* Document type dropdown */}
        <DropDown
          label="Type de document"
          options={DOCUMENT_TYPE_OPTIONS}
          value={docType}
          onSelect={(v) => setDocType(String(v))}
        />
      </div>

      {/* Footer */}
      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={uploading ? "Envoi..." : "Ajouter"}
          onClick={handleUpload}
          disabled={!file || uploading}
        />
      </div>
    </BaseModal>
  );
}

// ─── Document Preview Modal ───────────────────────────────

function DocumentPreviewModal({
  doc,
  url,
  onClose,
  onDownload,
}: {
  doc: { name: string; gcs_path?: string; created_at?: string };
  url: string;
  onClose: () => void;
  onDownload: () => void;
}) {
  const nameOrPath = doc.gcs_path || doc.name;
  const isImage = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i.test(nameOrPath);
  const isPdf = /\.pdf$/i.test(nameOrPath);

  // Image viewer state
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const zoomIn = () => setZoom((z) => Math.min(z * 1.3, 8));
  const zoomOut = () => setZoom((z) => Math.max(z / 1.3, 0.1));
  const resetView = () => { setZoom(1); setRotation(0); setPan({ x: 0, y: 0 }); };
  const rotateCW = () => setRotation((r) => (r + 90) % 360);
  const rotateCCW = () => setRotation((r) => (r - 90 + 360) % 360);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setPan({
      x: dragStart.current.panX + (e.clientX - dragStart.current.x),
      y: dragStart.current.panY + (e.clientY - dragStart.current.y),
    });
  };
  const handleMouseUp = () => setDragging(false);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isImage) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "+" || e.key === "=") zoomIn();
      else if (e.key === "-") zoomOut();
      else if (e.key === "0") resetView();
      else if (e.key === "r") rotateCW();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isImage]);

  const toolbarBtn = "flex items-center justify-center rounded-lg hover:bg-neutral-200 transition-colors";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: "80vw", maxWidth: 900, height: "85vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-row items-center justify-between px-5 py-3 border-b border-neutral-200">
          <div className="flex flex-col">
            <span className="text-h2 font-semibold">{doc.name}</span>
            <span className="text-h4 text-neutral-400">{doc.created_at}</span>
          </div>
          <div className="flex flex-row items-center gap-2">
            <button
              onClick={onDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50 text-h3 font-medium text-neutral-600"
            >
              <Icon1 name="download" size={14} color="#525252" />
              Télécharger
            </button>
            <button
              onClick={onClose}
              className={toolbarBtn}
              style={{ width: 32, height: 32 }}
            >
              <Icon1 name="close" size={16} color="#525252" />
            </button>
          </div>
        </div>

        {/* Image toolbar */}
        {isImage && (
          <div className="flex flex-row items-center justify-center gap-1 px-4 py-2 border-b border-neutral-200 bg-neutral-50">
            <button onClick={zoomOut} className={toolbarBtn} style={{ width: 32, height: 32 }} title="Dézoomer (-)">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M4 8h8" stroke="#525252" strokeWidth={1.5} strokeLinecap="round" /></svg>
            </button>
            <span className="text-h4 text-neutral-500 w-12 text-center select-none">{Math.round(zoom * 100)}%</span>
            <button onClick={zoomIn} className={toolbarBtn} style={{ width: 32, height: 32 }} title="Zoomer (+)">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M8 4v8M4 8h8" stroke="#525252" strokeWidth={1.5} strokeLinecap="round" /></svg>
            </button>
            <div className="w-px h-5 bg-neutral-300 mx-1" />
            <button onClick={rotateCCW} className={toolbarBtn} style={{ width: 32, height: 32 }} title="Rotation gauche">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M2 6.5A5.5 5.5 0 0 1 12.95 5M2 2.5V6.5H6" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" /><circle cx="8" cy="9" r="4.5" stroke="#525252" strokeWidth={1.2} fill="none" /></svg>
            </button>
            <button onClick={rotateCW} className={toolbarBtn} style={{ width: 32, height: 32 }} title="Rotation droite (R)">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M14 6.5A5.5 5.5 0 0 0 3.05 5M14 2.5V6.5H10" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" /><circle cx="8" cy="9" r="4.5" stroke="#525252" strokeWidth={1.2} fill="none" /></svg>
            </button>
            <div className="w-px h-5 bg-neutral-300 mx-1" />
            <button onClick={resetView} className={toolbarBtn + " px-2 gap-1"} style={{ height: 32 }} title="Réinitialiser (0)">
              <svg width={14} height={14} viewBox="0 0 14 14" fill="none"><path d="M1 7a6 6 0 1 1 1.46 3.93" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" /><path d="M1 11V7.5H4.5" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" /></svg>
              <span className="text-h4 text-neutral-500">1:1</span>
            </button>
          </div>
        )}

        {/* Content */}
        <div
          ref={containerRef}
          className="flex-1 overflow-hidden flex items-center justify-center bg-neutral-50"
          style={{ cursor: isImage ? (dragging ? "grabbing" : "grab") : "default" }}
          onWheel={isImage ? handleWheel : undefined}
          onMouseDown={isImage ? handleMouseDown : undefined}
          onMouseMove={isImage ? handleMouseMove : undefined}
          onMouseUp={isImage ? handleMouseUp : undefined}
          onMouseLeave={isImage ? handleMouseUp : undefined}
        >
          {isPdf ? (
            <iframe
              src={url}
              className="w-full h-full"
              style={{ border: "none" }}
              title={doc.name}
            />
          ) : isImage ? (
            <img
              src={url}
              alt={doc.name}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                transformOrigin: "center center",
                transition: dragging ? "none" : "transform 0.15s ease",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                userSelect: "none",
                pointerEvents: "none",
              }}
              draggable={false}
            />
          ) : (
            <div className="flex flex-col items-center gap-3 text-neutral-400">
              <Icon1 name="file" size={48} color="#D4D4D4" />
              <span className="text-h2 font-medium">Aperçu non disponible</span>
              <button
                onClick={onDownload}
                className="px-4 py-2 rounded-lg bg-indigo-500 text-white text-h3 font-medium hover:bg-indigo-600"
              >
                Télécharger le fichier
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CHAT_MEMBERS = [{ name: "M. Martin" }, { name: "Emeric" }, { name: "Mélanie" }];

// ─── Main screen ───────────────────────────────────────────

const CONTENT_TABS = [
  { value: "fil", label: "Fil du dossier" },
  { value: "contacts", label: "Contacts" },
  { value: "ordres", label: "Ordres" },
  { value: "vehicules", label: "Véhicules" },
  { value: "documents", label: "Documents" },
];

const TYPE_BADGE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  professionnel: { bg: "#10b981", text: "#FFFFFF", label: "Professionnel" },
  particulier: { bg: "#6366f1", text: "#FFFFFF", label: "Particulier" },
};

export default function DossierDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState("fil");
  const [search, setSearch] = useState("");
  const [fetchedDossier, setFetchedDossier] = useState<Dossier | null>(null);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<{ doc: { id: string; name: string; type: string; gcs_path?: string }; url: string } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [linkedOrdres, setLinkedOrdres] = useState<Ordre[]>([]);

  // Fetch fresh dossier data from API on mount
  useEffect(() => {
    if (!id) return;
    dossiersApi.get(id).then((d) => {
      setFetchedDossier(d);
    }).catch(() => {});
  }, [id]);

  const dossier = useMemo(() => {
    if (fetchedDossier) return getEnrichedDossier(id || "") ? { ...getEnrichedDossier(id || "")!, ...fetchedDossier } : fetchedDossier;
    return getEnrichedDossier(id || "");
  }, [id, fetchedDossier]);

  // Fetch full ordres linked to this dossier (to get their documents)
  useEffect(() => {
    if (!id) return;
    ordresApi.list({ dossier_id: id }).then((ordres) => {
      setLinkedOrdres(ordres);
    }).catch(() => {});
  }, [id]);

  const ordreDocGroups = useMemo(() => {
    return linkedOrdres
      .filter((o) => o.documents && o.documents.length > 0)
      .map((o) => {
        const v = o.vehicle;
        const vehicleName = v ? `${v.marque || ""} ${v.modele || ""}`.trim() || o.immatriculation || "Véhicule" : o.immatriculation || "Véhicule";
        return { vehicleName, ordreNum: o.num, ordreId: o.id, docs: o.documents };
      });
  }, [linkedOrdres]);

  // Flat list of all docs (dossier + ordres) for timeline thumbnail resolution
  const allDocs: AllDocEntry[] = useMemo(() => {
    const entries: AllDocEntry[] = [];
    if (dossier) {
      for (const d of dossier.documents || []) {
        entries.push({ id: d.id, name: d.name, gcs_path: d.gcs_path, parentType: "dossier", parentId: dossier.id });
      }
    }
    for (const o of linkedOrdres) {
      for (const d of o.documents || []) {
        entries.push({ id: d.id, name: d.name, gcs_path: d.gcs_path, parentType: "ordre", parentId: o.id });
      }
    }
    return entries;
  }, [dossier, linkedOrdres]);

  if (!dossier) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: "#FAFAFA" }}>
        <span className="text-h2 text-neutral-400">Dossier introuvable</span>
      </div>
    );
  }

  const openDocPreview = async (doc: { id: string; name: string; type: string; gcs_path?: string }) => {
    if (!doc.gcs_path) return;
    setLoadingPreview(true);
    try {
      const isOrdreDoc = doc.gcs_path.startsWith("ordres/");
      if (isOrdreDoc) {
        const parts = doc.gcs_path.split("/");
        const ordreId = parts[2];
        const url = await ordresApi.getDocumentUrl(ordreId, doc.id);
        setPreviewDoc({ doc, url });
      } else {
        const url = await dossiersApi.getDocumentUrl(dossier.id, doc.id);
        setPreviewDoc({ doc, url });
      }
    } catch {
      showNotification({ type: "error", message: "Impossible de charger l'aperçu" });
    } finally {
      setLoadingPreview(false);
    }
  };

  const openDocPreviewByName = (docName: string) => {
    const doc = (dossier.documents || []).find((d) => d.name === docName);
    if (doc) { openDocPreview(doc); return; }
    // Search in linked ordres' documents
    for (const o of linkedOrdres) {
      const ordreDoc = (o.documents || []).find((d) => d.name === docName);
      if (ordreDoc) { openDocPreview(ordreDoc); return; }
    }
  };

  const badge = TYPE_BADGE_STYLES[dossier.type] || TYPE_BADGE_STYLES.particulier;

  return (
    <div className="flex flex-row h-screen overflow-hidden" style={{ background: "#FAFAFA" }}>
      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-y-auto min-w-0">
        {/* Header */}
        <div className="flex flex-row items-start justify-between px-6 pt-8 pb-4">
          <div className="flex flex-col gap-2">
            <Text1>{dossier.name}</Text1>
            <span className="text-h4 font-medium px-3 py-1 rounded-full w-fit" style={{ background: badge.bg, color: badge.text }}>
              {badge.label}
            </span>
          </div>
          <MenuButton label="Actions">
            <div className="flex flex-col p-1">
              <button className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg hover:bg-neutral-50 text-h3 w-full text-left">
                <Icon1 name="modify" size={16} color="#525252" />
                Modifier le dossier
              </button>
              <button className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg hover:bg-neutral-50 text-h3 w-full text-left">
                <Icon1 name="side-ordres" size={16} color="#525252" />
                Créer un ordre
              </button>
              <button
                className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg hover:bg-neutral-50 text-h3 w-full text-left"
                onClick={() => setShowAddDoc(true)}
              >
                <Icon1 name="upload" size={16} color="#525252" />
                Ajouter un document
              </button>
              <button className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg hover:bg-neutral-50 text-h3 w-full text-left text-red-500">
                <Icon1 name="delete" size={16} color="#ef4444" />
                Supprimer
              </button>
            </div>
          </MenuButton>
        </div>

        {/* Tabs */}
        <div className="px-6 overflow-hidden">
          <ChunkyTabs activeTab={activeTab} tabs={CONTENT_TABS} setActiveTab={setActiveTab} />
        </div>

        {/* Search */}
        <div className="px-6 pt-3" style={{ maxWidth: 500 }}>
          <SearchField value={search} onChange={setSearch} placeholder="Chercher .." />
        </div>

        {/* Tab content */}
        <div className="px-6 pt-4 pb-8 flex-1 overflow-x-hidden">
          {activeTab === "fil" && <FilDuDossierTab timeline={dossier.timeline || []} vehicles={dossier.vehicles || []} search={search} onDocClick={openDocPreviewByName} allDocs={allDocs} />}
          {activeTab === "contacts" && <ContactsTab contacts={dossier.contacts || []} dossierId={dossier.id} />}
          {activeTab === "ordres" && <OrdresTab ordres={dossier.ordres || []} />}
          {activeTab === "vehicules" && <VehiculesTab vehicles={dossier.vehicles || []} dossierId={dossier.id} />}
          {activeTab === "documents" && <DocumentsTab documents={dossier.documents || []} ordreDocGroups={ordreDocGroups} dossierId={dossier.id} onPreview={openDocPreview} />}
        </div>
      </div>

      {/* Right chat panel — full on 2xl screens */}
      <div className="flex-shrink-0 hidden 2xl:block" style={{ width: 400 }}>
        <ChatPanel
          discussionId={dossier.discussion_id}
          internalDiscussionId={dossier.internal_discussion_id}
          entityType="dossier"
          entityId={dossier.id}
          entityName={dossier.name}
          members={CHAT_MEMBERS}
          onlineCount={2}
        />
      </div>

      {/* Floating chat widget — below xl only */}
      <FloatingChatWidget
        discussionId={dossier.discussion_id}
        internalDiscussionId={dossier.internal_discussion_id}
        entityType="dossier"
        entityId={dossier.id}
        entityName={dossier.name}
      />

      {/* Add document modal */}
      {showAddDoc && (
        <AddDocumentModal
          dossierId={dossier.id}
          onClose={() => setShowAddDoc(false)}
          onSuccess={(updated) => setFetchedDossier(updated)}
        />
      )}

      {/* Document preview modal */}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc.doc}
          url={previewDoc.url}
          onClose={() => setPreviewDoc(null)}
          onDownload={() => window.open(previewDoc.url, "_blank")}
        />
      )}
    </div>
  );
}
